"use client";

import React, { useState, useEffect } from "react";
import dynamic from "next/dynamic";

// Placeholder reserves exact space the canvas will occupy, preventing CLS
function TerrainPlaceholder() {
  return (
    <div
      className="fixed pointer-events-none -z-10"
      style={{
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        width: "100%",
        height: "100%",
        background: "transparent",
      }}
      aria-hidden="true"
    />
  );
}

// CSS gradient fallback for when WebGL fails
export function CSSGradientFallback() {
  return (
    <div
      className="fixed pointer-events-none -z-10"
      style={{
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        width: "100%",
        height: "100%",
        background:
          "linear-gradient(180deg, hsl(var(--background)) 0%, hsl(var(--muted)) 100%)",
        opacity: 0.5,
      }}
      aria-hidden="true"
    />
  );
}

// Error boundary for WebGL failures
class TerrainErrorBoundary extends React.Component<
  { children: React.ReactNode; fallback: React.ReactNode },
  { hasError: boolean }
> {
  constructor(props: { children: React.ReactNode; fallback: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.warn("WebGL terrain failed, using CSS fallback:", error, info);
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback;
    }
    return this.props.children;
  }
}

// Dynamic import wrapped with error boundary
const DynamicTerrain = dynamic(
  () =>
    import("@/components/backgrounds/terrain-background").then(
      (mod) => mod.TerrainBackground
    ),
  {
    ssr: false,
    loading: () => <TerrainPlaceholder />,
  }
);

export function TerrainBackground() {
  const [shouldLoad, setShouldLoad] = useState(false);

  // Defer loading Three.js until after the page load event so that LCP and TBT
  // are measured against page content rather than WebGL initialisation work.
  useEffect(() => {
    const onLoad = () => setShouldLoad(true);
    if (document.readyState === "complete") {
      setTimeout(onLoad, 0);
      return;
    }
    window.addEventListener("load", onLoad, { once: true });
    return () => window.removeEventListener("load", onLoad);
  }, []);

  return (
    <div aria-hidden="true" className="print:hidden">
      <TerrainErrorBoundary fallback={<CSSGradientFallback />}>
        {shouldLoad ? <DynamicTerrain /> : <TerrainPlaceholder />}
      </TerrainErrorBoundary>
    </div>
  );
}
